/** @prettier */
import { driver } from 'driver.js';
import { LOCATION_CHANGE, push } from 'react-router-redux';
import { delay } from 'redux-saga';

import {
  all,
  call,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { APP_LOADED } from '@xanadu/ui-shared/modules/application/actions';
import { isFuturesOrPredictionsProduct } from '@xanadu/ui-shared/modules/application/selectors';
import {
  setActiveMainTab as setBetslipActiveMainTab,
  setActiveTab as setBetslipActiveTab,
} from '@xanadu/ui-shared/modules/betslip/actions';
import { batchEventUpdate } from '@xanadu/ui-shared/modules/event/actions';
import * as eventsApi from '@xanadu/ui-shared/modules/event/api';
import { batchEventParticipantUpdate } from '@xanadu/ui-shared/modules/eventParticipants/actions';
import { marketUpdate } from '@xanadu/ui-shared/modules/market/actions';
import { updateAllOffersByServer } from '@xanadu/ui-shared/modules/openOffers/actions';
import * as openOffersApi from '@xanadu/ui-shared/modules/openOffers/api';
import {
  addUnsubmittedOrders,
  cleanUnsubmittedOrders,
} from '@xanadu/ui-shared/modules/portfolio/unsubmittedOrders/actions';
import { setPrices } from '@xanadu/ui-shared/modules/price/actions';
import { batchRunnerUpdate } from '@xanadu/ui-shared/modules/runner/actions';
import {
  addUnsubmittedOffers,
  cleanUnsubmittedOffers,
} from '@xanadu/ui-shared/modules/unsubmittedOffers/actions';
import { SET_CURRENCY } from '@xanadu/ui-shared/modules/user/actions';
import { registrationRequest } from '@xanadu/ui-shared/modules/userRegistration/actions';

import translatedConstants, {
  RESTRICTED_PAGES,
  updateTranslations,
} from '../../constants/customerOnboarding';

import shared18Next from '../../../../javascripts/i18n';
import { dangerouslyGetStore } from '../../configureStore';

import {
  REFRESH_ONBOARDING_TRANSLATIONS,
  START_TOUR_MODE,
  refreshOnboardingTranslations,
  setTourMode,
  toggleCustomerOnboardingPopup,
} from './actions';
import * as api from './api';

const { i18n = {} } = shared18Next || {};
const { t = (v) => v } = i18n;

function* showCustomerOnboarding() {
  let eligiblePage = RESTRICTED_PAGES.every(
    (path) => window.location.pathname.indexOf(path) === -1
  );

  if (eligiblePage) {
    yield put(
      toggleCustomerOnboardingPopup({
        visible: true,
        firstTimeVisitor: true,
      })
    );
  } else {
    while (!eligiblePage) {
      const { locationChange, tourStarted } = yield race({
        locationChange: take(LOCATION_CHANGE),
        tourStarted: take(START_TOUR_MODE),
      });

      if (tourStarted) {
        eligiblePage = true;
      }

      if (
        locationChange &&
        RESTRICTED_PAGES.every((path) => window.location.pathname.indexOf(path) === -1)
      ) {
        yield delay(600);
        yield put(
          toggleCustomerOnboardingPopup({
            visible: true,
            firstTimeVisitor: true,
          })
        );

        eligiblePage = true;
      }
    }
  }
}

function* handleCustomerOnboarding() {
  yield take(APP_LOADED);

  yield delay(2000);

  const onboardingEnabled = yield select((state) =>
    state.getIn(['application', 'onboardingEnabled'])
  );

  if (onboardingEnabled) {
    try {
      const onboardingAlreadyVisited = localStorage.getItem('mb-onboarding-visited');
      const userReturningTimes = localStorage.getItem('mb-user-returning-times');
      const showOnboarding = parseInt(userReturningTimes, 10) < 4;

      let isOneTrustCookiePopupClosed =
        !window.getCkyConsent ||
        (window.cookieyes &&
          typeof window.getCkyConsent === 'function' &&
          window.getCkyConsent().isUserActionCompleted);

      if (showOnboarding && (!onboardingAlreadyVisited || onboardingAlreadyVisited !== 'true')) {
        if (isOneTrustCookiePopupClosed) {
          yield call(showCustomerOnboarding);
        } else {
          const channel = yield call(api.createOneTrustChannel);

          while (!isOneTrustCookiePopupClosed) {
            yield take(channel);
            yield delay(400);
            yield call(showCustomerOnboarding);

            isOneTrustCookiePopupClosed = true;
          }
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error accessing localStorage', e.message);
    }
  }
}

/**
 * This saga is used to add fake event data related unsubmitted offers into the Redux store.
 * Still not 100% convinced where to put this saga
 */
function* handleFakeEventDataStorage({ eventParticipants, runners, markets, events }) {
  if (runners) {
    const formattedRunners = Object.keys(runners).map((key) => runners[key]);
    yield put(batchRunnerUpdate(formattedRunners));
    yield fork(put, setPrices(formattedRunners));
  }
  if (markets) yield put(marketUpdate(Object.keys(markets).map((key) => markets[key])));

  if (eventParticipants) {
    yield put(
      batchEventParticipantUpdate(
        Object.keys(eventParticipants).map((key) => eventParticipants[key])
      )
    );
  }

  if (events) {
    const eventIds = Object.keys(events);

    yield put(batchEventUpdate(eventIds.map((key) => events[key])));

    return eventIds;
  }

  return [];
}

function* handleTourMode() {
  const { DRIVER_STEPS = [] } = translatedConstants;
  const isLoggedIn = yield select((state) => state.getIn(['user', 'isLoggedIn']));

  const store = dangerouslyGetStore();

  const wizard = driver();

  const onDestroyed = () => {
    store.dispatch(cleanUnsubmittedOffers());
    store.dispatch(
      setTourMode({
        tourMode: false,
      })
    );

    track('Marketing', 'Tour closed');
  };

  const onNextClick = (_, __, config) => {
    const { state: driverState } = config;

    track('Marketing', `Tour Step ${driverState.activeIndex + 1} Viewed`);

    if (driverState.activeIndex === 3) {
      store.dispatch(setBetslipActiveTab('active'));
    }

    if (driverState.activeIndex === 4) {
      if (!isLoggedIn) {
        store.dispatch(registrationRequest());

        track('Marketing', 'Reg opened from Tour');
      }
    }

    wizard.moveNext();
  };

  const onPrevClick = (_, __, config) => {
    const { state: driverState } = config;

    if (driverState.activeIndex === 4) {
      store.dispatch(setBetslipActiveTab('place'));
    }

    wizard.movePrevious();
  };

  try {
    wizard.setConfig({
      popoverClass: 'driverjs-theme driverjs-theme--exchange',
      doneBtnText: !isLoggedIn ? t('Join Now') : t('End Tour'),
      prevBtnText: t('Previous'),
      steps: DRIVER_STEPS,
      allowClose: false,
      showProgress: false,
      popoverOffset: 8,
      stagePadding: 8,
      stageRadius: 4,
      disableActiveInteraction: true,
      onDestroyed,
      onNextClick,
      onPrevClick,
    });

    yield call(wizard.drive);

    const onClosingOnboarding = (event) => {
      const isOnboardingCloseButton = event.target.classList.contains('onboarding-close-btn');

      if (isOnboardingCloseButton) {
        wizard.destroy();

        document.removeEventListener('click', onClosingOnboarding);
      }
    };

    // resize throttle
    let interval = null;

    const onWindowResize = () => {
      clearInterval(interval);

      interval = setInterval(() => {
        wizard.refresh();
        clearInterval(interval);
      }, 400);

      if (window.innerWidth < 1200) {
        wizard.destroy();

        window.removeEventListener('resize', onWindowResize);
      }
    };

    document.addEventListener('click', onClosingOnboarding);
    window.addEventListener('resize', onWindowResize);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error handling the tour', error.message);
  }
}

function* handlePredictionsTourMode({ theme = 'light' }) {
  const { PREDICTIONS_DRIVER_STEPS = [] } = translatedConstants;
  const isLoggedIn = yield select((state) => state.getIn(['user', 'isLoggedIn']));

  const store = dangerouslyGetStore();

  const wizard = driver();

  const onDestroyed = () => {
    store.dispatch(cleanUnsubmittedOrders());
    store.dispatch(
      setTourMode({
        tourMode: false,
      })
    );

    track('Marketing', 'Tour closed');
  };

  const onNextClick = (_, __, config) => {
    const { state: driverState } = config;

    track('Marketing', `Tour Step ${driverState.activeIndex + 1} Viewed`);

    if (driverState.activeIndex === 1) {
      store.dispatch(cleanUnsubmittedOrders());

      store.dispatch(
        addUnsubmittedOrders([
          {
            cid: 'ob-wizard-predictions-order-back',
            side: 'win',
            runnerId: 'ob-wizard-predictions-runner-id',
            eventId: 'ob-wizard-predictions-event-id',
            backersStake: 10,
          },
        ])
      );
    }

    if (driverState.activeIndex === 2) {
      if (!isLoggedIn) {
        store.dispatch(registrationRequest());

        track('Marketing', 'Reg opened from Tour');
      }
    }

    wizard.moveNext();
  };

  const onPrevClick = (_, __, config) => {
    const { state: driverState } = config;

    if (driverState.activeIndex === 2) {
      store.dispatch(cleanUnsubmittedOrders());
    }

    wizard.movePrevious();
  };

  try {
    wizard.setConfig({
      popoverClass: 'driverjs-theme driverjs-theme--predictions',
      doneBtnText: !isLoggedIn ? t('Join Now') : t('End Tour'),
      prevBtnText: t('Previous'),
      steps: PREDICTIONS_DRIVER_STEPS,
      allowClose: false,
      showProgress: false,
      popoverOffset: 8,
      stagePadding: 0,
      stageRadius: 0,
      disableActiveInteraction: true,
      onNextClick,
      onPrevClick,
      onDestroyed,
      ...(theme === 'light'
        ? {
            // eslint-disable-next-line indent-legacy
            overlayColor: 'rgb(255, 255, 255)',
            // eslint-disable-next-line indent-legacy
            overlayOpacity: '0.8',
            // eslint-disable-next-line indent-legacy
          }
        : {}),
    });

    yield call(wizard.drive);

    const onClosingOnboarding = (event) => {
      const isOnboardingCloseButton = event.target.classList.contains('onboarding-close-btn');

      if (isOnboardingCloseButton) {
        wizard.destroy();

        document.removeEventListener('click', onClosingOnboarding);
      }
    };

    // resize throttle
    let interval = null;

    const onWindowResize = () => {
      clearInterval(interval);

      interval = setInterval(() => {
        wizard.refresh();
        clearInterval(interval);
      }, 400);

      if (window.innerWidth < 1200) {
        wizard.destroy();

        window.removeEventListener('resize', onWindowResize);
      }
    };

    document.addEventListener('click', onClosingOnboarding);
    window.addEventListener('resize', onWindowResize);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error handling the predictions tour', error.message);
  }
}

function* watchForTourMode({ theme }) {
  while (true) {
    yield take(START_TOUR_MODE);

    const isFuturesOrPredictions = yield select(isFuturesOrPredictionsProduct);

    if (isFuturesOrPredictions) {
      const { events, markets, runners, eventParticipants } = yield call(
        api.loadFakePredictionsEvent
      );

      yield call(handleFakeEventDataStorage, {
        eventParticipants,
        runners,
        markets,
        events,
      });
    } else {
      const { events, markets, runners, eventParticipants } = yield call(eventsApi.loadFakeEvent);

      const offersResponse = yield call(openOffersApi.loadFakeOpenOffer);

      yield call(handleFakeEventDataStorage, {
        eventParticipants,
        runners,
        markets,
        events,
      });

      yield put(updateAllOffersByServer(offersResponse.offers, { fakeData: true }));

      yield put(setBetslipActiveMainTab('singles'));

      yield delay(200);

      yield put(
        addUnsubmittedOffers([
          {
            cid: 'ob-wizard-offer-lay',
            side: 'lay',
            runnerId: 'ob-wizard-runner-id-1',
            eventId: 'ob-wizard-event-id',
            backersStake: 20,
          },
        ])
      );

      yield delay(200);

      yield put(
        addUnsubmittedOffers([
          {
            cid: 'ob-wizard-offer-back',
            side: 'back',
            runnerId: 'ob-wizard-runner-id-1',
            eventId: 'ob-wizard-event-id',
            backersStake: 20,
          },
        ])
      );
    }

    if (window.location.pathname !== '/') {
      yield put(push('/'));

      yield delay(1000);
    }

    yield put(setTourMode({ tourMode: true }));

    yield fork(isFuturesOrPredictions ? handlePredictionsTourMode : handleTourMode, { theme });
  }
}

function* watchRefreshOnboardingTranslations() {
  yield call(updateTranslations);
}

function* updateTranslationsOnCurrencyChange() {
  yield put(refreshOnboardingTranslations());
}

export default function* root({ customization = {} }) {
  const { CustomerOnBoarding: { theme } = {} } = customization;

  yield all([
    fork(handleCustomerOnboarding),
    fork(watchForTourMode, { theme }),
    takeLatest(REFRESH_ONBOARDING_TRANSLATIONS, watchRefreshOnboardingTranslations),
    takeEvery(SET_CURRENCY, updateTranslationsOnCurrencyChange),
  ]);
}
